/* Coverly product gallery — rail paging + fullscreen lightbox.

   Lifted out of the snippet so it is cached and parsed once. Registration is
   guarded, so a second render of the snippet (Dawn duplicates the gallery when
   media sits on the right) upgrades rather than redefines. */
(function () {
  if (customElements.get('coverly-gallery')) return;

  class CoverlyGallery extends HTMLElement {
    connectedCallback() {
      if (this._wired) return;
      this._wired = true;

      this.viewport = this.querySelector('[data-cvg-viewport]');
      this.track    = this.querySelector('[data-cvg-track]');
      this.slides   = Array.from(this.querySelectorAll('[data-cvg-slide]:not([hidden])'));
      this.counter  = this.querySelector('[data-cvg-counter]');
      this.prevBtn  = this.querySelector('[data-cvg-prev]');
      this.nextBtn  = this.querySelector('[data-cvg-next]');
      this.total    = this.slides.length;

      // Natural shape has no fixed aspect, so the hero box has to be resized
      // to whichever image is showing — see fitNatural.
      this.natural     = !!this.querySelector('.cvg__slide--natural');
      this.strip       = this.querySelector('[data-cvg-strip]');
      this.stripThumbs = Array.from(this.querySelectorAll('[data-cvg-strip-thumb]'));

      this.lightbox = this.querySelector('[data-cvg-lightbox]');
      this.stack    = this.querySelector('[data-cvg-stack]');
      this.thumbs   = Array.from(this.querySelectorAll('[data-cvg-thumb]'));

      if (!this.viewport) return;

      this.prevBtn && this.prevBtn.addEventListener('click', () => this.page(-1));
      this.nextBtn && this.nextBtn.addEventListener('click', () => this.page(1));
      this.viewport.addEventListener('scroll', () => this.onScroll(), { passive: true });

      this.stripThumbs.forEach((t) => {
        t.addEventListener('click', () => this.goTo(+t.dataset.index));
      });

      var openBtn = this.querySelector('[data-cvg-open-zoom]');
      openBtn && openBtn.addEventListener('click', () => this.openZoom(this.currentIndex()));
      this.slides.forEach((s) => {
        s.addEventListener('click', () => this.openZoom(+s.dataset.index));
        s.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.openZoom(+s.dataset.index); }
        });
      });

      if (this.lightbox) {
        var closeBtn = this.querySelector('[data-cvg-close]');
        closeBtn && closeBtn.addEventListener('click', () => this.closeZoom());
        this.lightbox.addEventListener('click', (e) => {
          if (e.target === this.lightbox) this.closeZoom();
        });
        // Escape normally closes a modal dialog on its own; doing it explicitly
        // costs nothing and covers engines that deliver the key without a code.
        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && this.lightbox.open) { e.preventDefault(); this.closeZoom(); }
        });
        this.thumbs.forEach((t) => t.addEventListener('click', () => this.scrollStackTo(+t.dataset.index)));
        this.stack && this.stack.addEventListener('scroll', () => this.syncRail(), { passive: true });
      }

      // A lazily-loaded image has no intrinsic size until it arrives, so the
      // first measurement of it would be wrong.
      if (this.natural) {
        this.slides.forEach((sl) => {
          var im = sl.querySelector('img');
          if (im && !im.complete) im.addEventListener('load', () => this.fitNatural());
        });
      }

      this.update();
      window.addEventListener('resize', () => this.update());
    }

    // Distance between slide origins — the real paging unit. Using the
    // viewport width instead would skip 1.5 slides whenever the rail is
    // configured to show a peek.
    step() {
      if (this.slides.length < 2) return this.slides[0] ? this.slides[0].offsetWidth : 0;
      return this.slides[1].offsetLeft - this.slides[0].offsetLeft;
    }
    currentIndex() {
      var s = this.step();
      return s ? Math.round(this.viewport.scrollLeft / s) : 0;
    }
    page(dir) {
      this.goTo(this.currentIndex() + dir);
    }

    // Absolute target rather than scrollBy, so a cancelled animation cannot
    // leave the rail half-paged. Shared by the arrows and the thumbnails.
    goTo(index) {
      var vp = this.viewport;
      var s = this.step() || vp.clientWidth;
      var next = Math.min(Math.max(index, 0), this.total - 1);
      var target = next * s;
      var from = vp.scrollLeft;
      if (target === from) return;

      vp.scrollTo({ left: target, behavior: 'smooth' });

      // A mandatory-snap container cancels animated scrolls outright in some
      // engines. If nothing has moved at all by the next frames the animation
      // never started, so land it instantly rather than leaving dead arrows.
      // Any movement means an animation is running — leave it alone.
      var self = this;
      clearTimeout(this._pageT);
      this._pageT = setTimeout(function () {
        // Any movement means an animation is genuinely running; leave it be.
        // Dead still after 120ms means it was cancelled, so land it outright.
        if (vp.scrollLeft === from) vp.scrollLeft = target;
        self.update();
      }, 120);
      this.update();
    }
    onScroll() {
      clearTimeout(this._t);
      this._t = setTimeout(() => this.update(), 60);
    }
    update() {
      var max = this.viewport.scrollWidth - this.viewport.clientWidth - 2;
      var atEnd = this.viewport.scrollLeft >= max;
      // With a fractional per-view the final slide is never the leftmost, so
      // the count would stall one short of the total. At the end of the rail
      // the last image is on screen — say so.
      var idx = atEnd ? this.total - 1 : Math.min(this.currentIndex(), this.total - 1);
      if (this.counter) this.counter.textContent = (idx + 1) + ' / ' + this.total;
      if (this.prevBtn) this.prevBtn.disabled = this.viewport.scrollLeft <= 2;
      if (this.nextBtn) this.nextBtn.disabled = atEnd;
      this.syncStrip(idx);
      this.fitNatural();
    }

    // Collapses the hero box onto the active image's own proportions, capped by
    // whatever height the CSS allows so the thumbnail strip stays reachable.
    // Clearing the inline height first makes clientHeight report that cap.
    fitNatural() {
      var vp = this.viewport;
      if (!vp) return;
      vp.style.removeProperty('height');
      if (!this.natural) return;
      if (!window.matchMedia('(min-width: 990px)').matches) return;

      var slide = this.slides[Math.min(this.currentIndex(), this.total - 1)];
      var img = slide && slide.querySelector('img');
      if (!img || !img.naturalWidth) return;

      var cap = vp.clientHeight;
      var w = slide.clientWidth || vp.clientWidth;
      var h = Math.min((w * img.naturalHeight) / img.naturalWidth, cap);
      if (h > 0) vp.style.height = Math.round(h) + 'px';
    }

    syncStrip(idx) {
      if (!this.stripThumbs.length) return;
      var active = null;
      this.stripThumbs.forEach((t) => {
        var on = +t.dataset.index === idx;
        t.setAttribute('aria-current', String(on));
        if (on) active = t;
      });
      if (!active || !this.strip) return;

      // Only on an actual change of selection, so a visitor scrubbing the strip
      // by hand is not yanked back while the hero stays put.
      if (idx === this._stripIdx) return;
      this._stripIdx = idx;

      // Centre the active thumbnail rather than merely scrolling it into view.
      // Nudging it just far enough to be visible means clicking the leftmost
      // thumbnail moves nothing, stranding every earlier image off-screen with
      // the arrows as the only way back. Centring always exposes neighbours on
      // both sides. The browser clamps at the ends, so the first and last
      // thumbnails still rest against their edge.
      var sr = this.strip.getBoundingClientRect();
      var ar = active.getBoundingClientRect();
      if (this.strip.scrollHeight > this.strip.clientHeight + 1) {
        this.strip.scrollTop += (ar.top - sr.top) - (sr.height - ar.height) / 2;
      } else {
        this.strip.scrollLeft += (ar.left - sr.left) - (sr.width - ar.width) / 2;
      }
    }

    // The visualizer tile reveals itself only once its catalogue resolves, so
    // the slide count has to be recomputed rather than read once at connect.
    refresh() {
      this.slides = Array.from(this.querySelectorAll('[data-cvg-slide]:not([hidden])'));
      this.total = this.slides.length;
      this.update();
    }

    openZoom(index) {
      // -1 is the visualizer tile: interactive, so there is nothing to enlarge
      // and an empty lightbox would read as a bug.
      if (index < 0) return;
      if (!this.lightbox) return;
      if (typeof this.lightbox.showModal === 'function') this.lightbox.showModal();
      else this.lightbox.setAttribute('open', '');
      requestAnimationFrame(() => this.scrollStackTo(index, 'auto'));
    }
    closeZoom() {
      if (!this.lightbox) return;
      if (this.lightbox.open) this.lightbox.close();
      else this.lightbox.removeAttribute('open');
    }
    scrollStackTo(index, behavior) {
      if (!this.stack) return;
      var item = this.stack.querySelector('[data-cvg-stack-item][data-index="' + index + '"]');
      if (!item) return;
      this.stack.scrollTo({ top: item.offsetTop - this.stack.offsetTop, behavior: behavior || 'smooth' });
      this.setRail(index);
    }
    syncRail() {
      var items = Array.from(this.stack.querySelectorAll('[data-cvg-stack-item]'));
      var mid = this.stack.scrollTop + this.stack.clientHeight / 2;
      var active = 0;
      items.forEach((it) => { if (it.offsetTop - this.stack.offsetTop <= mid) active = +it.dataset.index; });
      this.setRail(active);
    }
    setRail(index) {
      this.thumbs.forEach((t) => t.setAttribute('aria-current', (+t.dataset.index === index) ? 'true' : 'false'));
    }
  }

  customElements.define('coverly-gallery', CoverlyGallery);
})();
